/**
 * SummarizedListSectionTemplate — optional summary above primary content, with loading/error shells.
 * Composes Typography + IconWrapper for built-in states; ready body is slot-driven.
 */
import type { ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { Typography } from "../../atoms/Typography";
import { IconWrapper } from "../../atoms/IconWrapper";
import type { SummarizedListSectionTemplateProps } from "./SummarizedListSectionTemplate.types";
import {
  summarizedListSectionErrorBodyClass,
  summarizedListSectionErrorIconWellClass,
  summarizedListSectionErrorRootClass,
  summarizedListSectionLoadingRootClass,
  summarizedListSectionLoadingSpinnerClass,
  summarizedListSectionReadyStackClass,
} from "./SummarizedListSectionTemplate.styles";

export type {
  SummarizedListSectionGap,
  SummarizedListSectionStatus,
  SummarizedListSectionTemplateProps,
} from "./SummarizedListSectionTemplate.types";

function TemplateLoading({ message }: { message: string }) {
  return (
    <div className={summarizedListSectionLoadingRootClass}>
      <div className={summarizedListSectionLoadingSpinnerClass} />
      <Typography variant="body-sm" color="secondary">
        {message}
      </Typography>
    </div>
  );
}

function TemplateError({
  title,
  message,
  icon,
}: {
  title: string;
  message: string;
  icon: ReactNode;
}) {
  return (
    <div className={summarizedListSectionErrorRootClass}>
      <div className={summarizedListSectionErrorBodyClass}>
        <div className={summarizedListSectionErrorIconWellClass}>{icon}</div>
        <div>
          <Typography variant="h3" weight="bold" color="primary">
            {title}
          </Typography>
          <Typography variant="body-sm" color="secondary">
            {message}
          </Typography>
        </div>
      </div>
    </div>
  );
}

export function SummarizedListSectionTemplate({
  status,
  loadingMessage = "Loading…",
  errorTitle = "Something went wrong",
  errorMessage = "",
  errorIcon,
  summary,
  sectionGap = "normal",
  primary,
  className,
}: SummarizedListSectionTemplateProps) {
  if (status === "loading") {
    return <TemplateLoading message={loadingMessage} />;
  }

  if (status === "error") {
    return (
      <TemplateError
        title={errorTitle}
        message={errorMessage}
        icon={
          errorIcon ?? (
            <IconWrapper size="md" color="error">
              <AlertCircle />
            </IconWrapper>
          )
        }
      />
    );
  }

  return (
    <div className={summarizedListSectionReadyStackClass(sectionGap, className)}>
      {summary}
      {primary ?? null}
    </div>
  );
}
